import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0Dialog, PersonAvatarVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  CommunityMemberEmployeeFragment,
  GetCommunityMembersDocument,
  GroupEmployeeIdsDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'

type Props = {
  groupId: number
  groupTitle: string
  initialAvatars: PersonAvatarVariant[]
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  showTrigger?: boolean
}

type GroupEmployee = CommunityMemberEmployeeFragment

const getEmployeeIdsFromGroupData = (
  data:
    | { posts?: { group?: { employees?: Array<{ id?: number }> } | null } | null }
    | null
    | undefined
): number[] | null => {
  if (!!data?.posts?.group?.employees?.length) {
    return data.posts.group.employees
      .map((employee) => employee.id)
      .filter((id): id is number => id !== undefined)
  }

  return null
}

export const MembersList: React.FC<Props> = ({
  groupId,
  groupTitle,
  initialAvatars,
  isOpen: controlledIsOpen,
  onOpenChange,
  showTrigger = true,
}) => {
  const initialTotalCount = initialAvatars.length
  const [internalIsOpen, setInternalIsOpen] = React.useState(false)
  const [employeeIds, setEmployeeIds] = React.useState<number[] | null>(null)

  const isOpen = controlledIsOpen ?? internalIsOpen
  const setIsOpen = React.useCallback(
    (value: boolean) => {
      if (controlledIsOpen === undefined) {
        setInternalIsOpen(value)
      }
      onOpenChange?.(value)
    },
    [controlledIsOpen, onOpenChange]
  )

  const { data: groupData } = useQuery(GroupEmployeeIdsDocument, {
    variables: {
      id: groupId,
    },
    skip: !isOpen,
    fetchPolicy: 'cache-first',
  })

  React.useEffect(() => {
    if (isOpen && groupData) {
      const ids = getEmployeeIdsFromGroupData(groupData)
      setEmployeeIds(ids)
    } else if (!isOpen) {
      setEmployeeIds(null)
    }
  }, [isOpen, groupData])

  const dataSource = useGraphqlDataCollectionSource(
    {
      document: GetCommunityMembersDocument,
      mapData: (data) =>
        data.employees.employeesConnection.edges
          .map((edge) => edge.node)
          .filter((node): node is GroupEmployee => node !== null),
      mapVariables: ({ search }) => ({
        employeeIds: employeeIds && employeeIds.length > 0 ? employeeIds : [],
        fuzzySearch: search || undefined,
      }),
      mapPagination: (data) => ({
        total: data.employees.employeesConnection.totalCount ?? 0,
        cursor: data.employees.employeesConnection.pageInfo.endCursor ?? undefined,
        hasNextPage: data.employees.employeesConnection.pageInfo.hasNextPage ?? false,
      }),
      paginationType: 'infinite-scroll',
      search: {
        enabled: true,
      },
    },
    [employeeIds]
  )

  const modalOpen = !!employeeIds?.length

  const handleCloseModal = React.useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const MAX_VISIBLE_AVATARS = 3
  const visibleAvatars = initialAvatars.slice(0, MAX_VISIBLE_AVATARS)
  const remainingCount =
    initialTotalCount > MAX_VISIBLE_AVATARS ? initialTotalCount - MAX_VISIBLE_AVATARS : 0

  return (
    <>
      {showTrigger && (
        <Box cursor="pointer" onClick={() => setIsOpen(true)} width="fit-content">
          <F0AvatarList
            avatars={visibleAvatars}
            remainingCount={remainingCount}
            size="sm"
            type="person"
          />
        </Box>
      )}
      <F0Dialog
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={i18n.t('posts.communities.members_title', {
          membersCount: initialTotalCount,
          groupTitle,
        })}
      >
        {modalOpen && employeeIds && employeeIds.length > 0 && (
          <Box style={{ height: '400px', overflowY: 'auto' }}>
            <OneDataCollection
              source={dataSource}
              emptyStates={{
                'no-data': {
                  title: i18n.t('posts.communities.no_result'),
                },
              }}
              visualizations={[
                {
                  type: 'table',
                  options: {
                    columns: [
                      {
                        label: i18n.t('posts.communities.members_name'),
                        render: (item: GroupEmployee) => {
                          const avatarUrl = buildAvatarUrl(item.avatar)
                          return {
                            type: 'person',
                            value: {
                              firstName: item.firstName ?? '',
                              lastName: item.lastName ?? '',
                              src: avatarUrl,
                            },
                          }
                        },
                      },
                      {
                        label: i18n.t('posts.communities.members_role'),
                        render: (item: GroupEmployee) => {
                          const role = item?.referenceContract?.jobCatalogLevel?.roleName ?? ''
                          return {
                            type: 'text',
                            value: role,
                          }
                        },
                      },
                    ],
                  },
                },
              ]}
            />
          </Box>
        )}
      </F0Dialog>
    </>
  )
}
